import React, { useState, useEffect, useRef } from "react";
import { Box, Button } from "@mui/material";
import { motion, AnimatePresence, useViewportScroll, useTransform } from "framer-motion";
import { Check, ArrowRight, Search, MapPin, Download } from "lucide-react";
import ProfilePicture1 from "pictures/profile_picture_1_recruiter.jpg";
import ProfilePicture2 from "pictures/profile_picture_2_recruiter.jpg";
import ProfilePicture3 from "pictures/profile_picture_3_recruiter.jpg";
import ProfilePicture4 from "pictures/profile_picture_4_recruiter.png";
import ProfilePicture5 from "pictures/profile_picture_5_recruiter.png";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";



const formatNumber = (n) => n.toLocaleString("fr-BE");

function useCountUp(target, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(!startOnView);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startOnView]);

  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return { count, ref };
}

const StatCounter = ({ value, label }) => {
  const { count, ref } = useCountUp(value, 2000);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        color: "hsla(0, 0%, 98%, 0.8)",
      }}
    >
      <Box
        sx={{
          width: "20px",
          height: "20px",
          borderRadius: "9999px",
          bgcolor: "hsla(145, 63%, 42%, 0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Check size={12} color="hsl(145, 63%, 42%)" />
      </Box>
      <Box
        component="span"
        ref={ref}
        sx={{
          fontSize: { xs: "0.875rem", sm: "1rem" },
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
        }}
      >
        <Box
          component="strong"
          sx={{
            color: "hsl(0, 0%, 98%)",
            fontFamily: "Segoe UI Bold, system-ui, sans-serif",
          }}
        >
          {formatNumber(count)}
        </Box>{" "}
        {label}
      </Box>
    </Box>
  );
};

const RecruiterHero = ({ candidatesCount = 0, jobApplicationsCount = 0, companiesCount = 0 }) => {
  const intl = useIntl();

  const dashboardTabs = [
    { label: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.tab_dashboard" }), active: false },
    { label: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.tab_applications" }), active: false },
    { label: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.tab_offers" }), active: false },
    { label: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.tab_cv_library" }), active: true },
    { label: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.tab_admin" }), active: false },
  ];

  const candidates = [
    {
      avatar: ProfilePicture1,
      name: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand1_name" }),
      role: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand1_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand1_type" }),
      exp: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand1_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand1_location" }),
    },
    {
      avatar: ProfilePicture2,
      name: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand2_name" }),
      role: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand2_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand2_type" }),
      exp: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand2_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand2_location" }),
    },
    {
      avatar: ProfilePicture4,
      name: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand3_name" }),
      role: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand3_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand3_type" }),
      exp: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand3_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand3_location" }),
    },
    {
      avatar: ProfilePicture3,
      name: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand4_name" }),
      role: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand4_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand4_type" }),
      exp: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand4_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand4_location" }),
    },
    {
      avatar: ProfilePicture5,
      name: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand5_name" }),
      role: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand5_role" }),
      type: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand5_type" }),
      exp: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand5_exp" }),
      location: intl.formatMessage({ id: "landing_recruiter.recruiter_hero.cand5_location" }),
    },
  ];

  const rotatingWords = [
    intl.formatMessage({ id: "landing_recruiter.recruiter_hero.rotating_word_1" }),
    intl.formatMessage({ id: "landing_recruiter.recruiter_hero.rotating_word_2" }),
    intl.formatMessage({ id: "landing_recruiter.recruiter_hero.rotating_word_3" }),
    intl.formatMessage({ id: "landing_recruiter.recruiter_hero.rotating_word_4" }),
  ];
  const statsData = [
    {
      value: candidatesCount,
      label: intl.formatMessage({
        id: "landing_recruiter.recruiter_hero.stat_candidates",
      }),
    },
    {
      value: jobApplicationsCount,
      label: intl.formatMessage({
        id: "landing_recruiter.recruiter_hero.stat_applications",
      }),
    },
    {
      value: companiesCount,
      label: intl.formatMessage({
        id: "landing_recruiter.recruiter_hero.stat_employers",
      }),
    },
  ];

  const i18nLocale = useSelector((state) => state.i18nLocale);
  const [wordIndex, setWordIndex] = useState(0);
  const [activeCandidate, setActiveCandidate] = useState(0);
  const sectionRef = useRef(null);
  const { scrollY } = useViewportScroll();
  const mockupY = useTransform(scrollY, [0, 800], [0, 80]);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCandidate((prev) => (prev + 1) % candidates.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      component="section"
      ref={sectionRef}
      sx={{
        position: "relative",
        minHeight: "90vh",
        display: "flex",
        alignItems: "center",
        bgcolor: "hsl(178, 93%, 10%)",
        overflow: "hidden",
      }}
    >
      {/* Animated gradient orbs */}
      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 30, 0],
          opacity: [0.15, 0.3, 0.15],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        sx={{
          position: "absolute",
          top: "25%",
          right: "-160px",
          width: "500px",
          height: "500px",
          borderRadius: "9999px",
          bgcolor: "hsla(145, 63%, 42%, 0.2)",
          filter: "blur(120px)",
        }}
      />
      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          delay: 3,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          bottom: "-128px",
          left: "-128px",
          width: "400px",
          height: "400px",
          borderRadius: "9999px",
          bgcolor: "hsla(145, 63%, 42%, 0.1)",
          filter: "blur(100px)",
        }}
      />

      {/* Grid pattern */}
      <Box sx={{ position: "absolute", inset: 0, opacity: 0.03 }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </Box>

      {/* Main content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: "16px", sm: "24px", lg: "32px" },
          py: "80px",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: "48px",
            alignItems: "center",
          }}
        >
          {/* Left */}
          <Box>
            <Box
              component={motion.h1}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              sx={{
                fontSize: {
                  xs: "1.875rem",
                  sm: "2.25rem",
                  md: "3rem",
                  lg: "3.75rem",
                },
                fontFamily: "Segoe UI Black, system-ui, sans-serif",
                color: "hsl(0, 0%, 98%)",
                lineHeight: 1.08,
                letterSpacing: "-0.025em",
                mt: 0,
                mx: 0,
                mb: "24px",
              }}
            >
              <FormattedMessage id="landing_recruiter.recruiter_hero.title_prefix" />{" "}
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  position: "relative",
                  overflow: "hidden",
                  verticalAlign: "bottom",
                  height: "calc(1.2em + 4px)",
                  minWidth: "14ch",
                }}
              >
                <AnimatePresence mode="wait">
                  <Box
                    component={motion.span}
                    key={rotatingWords[wordIndex]}
                    initial={{ opacity: 0, y: 50, rotateX: -40 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    exit={{ opacity: 0, y: -50, rotateX: 40 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    sx={{
                      display: "inline-block",
                      fontFamily: "Segoe UI Black, system-ui, sans-serif",
                      px: "1rem",
                      py: "0.25rem",
                      background:
                        "linear-gradient(to right, hsl(153 68% 25%), hsl(153 68% 25% / 0.8))",
                      borderRadius: "0.75rem",
                      color: "white",
                      position: "absolute",
                      top: 0,
                      left: 0,
                    }}
                  >
                    {rotatingWords[wordIndex]}
                  </Box>
                </AnimatePresence>
              </Box>
            </Box>

            <Box
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                mb: "32px",
              }}
            >
              {statsData.map((stat) => (
                <StatCounter
                  key={stat.label}
                  value={stat.value}
                  label={stat.label}
                />
              ))}
            </Box>

            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}
            >
              <Button
                component="a"
                href={`/${i18nLocale}/recruiters/sign_up`}
                sx={{
                  bgcolor: "hsl(153 68% 25%)",
                  color: "white",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontSize: "0.875rem",
                  px: "1.5rem",
                  py: "0.875rem",
                  borderRadius: "0.75rem",
                  textTransform: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  lineHeight: 1.5,
                  textDecoration: "none",
                  "&:hover": {
                    bgcolor: "hsl(153 68% 25% / 0.9)",
                    textDecoration: "none",
                  },
                }}
              >
                <FormattedMessage id="landing_recruiter.recruiter_hero.cta_create_account" />
                <ArrowRight size={14} className="arrow-icon" />
              </Button>
              <Button
                component="a"
                href={`/${i18nLocale}/`}
                sx={{
                  bgcolor: "transparent",
                  color: "hsl(0, 0%, 98%)",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontSize: "0.875rem",
                  px: "1.5rem",
                  py: "0.875rem",
                  borderRadius: "0.75rem",
                  textTransform: "none",
                  border: "1px solid hsla(0, 0%, 98%, 0.2)",
                  lineHeight: 1.5,
                  textDecoration: "none",
                  "&:hover": {
                    bgcolor: "hsla(0, 0%, 98%, 0.05)",
                    borderColor: "hsla(0, 0%, 98%, 0.3)",
                    textDecoration: "none",
                  },
                }}
              >
                <FormattedMessage id="landing_recruiter.recruiter_hero.cta_candidate" />
              </Button>
            </Box>
          </Box>

          {/* Right — CV-Thèque Preview */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            style={{ y: mockupY }}
            sx={{ display: { xs: "none", lg: "block" } }}
          >
            <Box
              sx={{
                borderRadius: "1rem",
                border: "1px solid hsla(0, 0%, 98%, 0.1)",
                bgcolor: "hsla(0, 0%, 98%, 0.03)",
                backdropFilter: "blur(16px)",
                overflow: "hidden",
              }}
            >
              {/* Top nav tabs */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  borderBottom: "1px solid hsla(0, 0%, 98%, 0.1)",
                  px: "24px",
                  overflow: "hidden",
                }}
              >
                {dashboardTabs.map((tab) => (
                  <Box
                    component="span"
                    key={tab.label}
                    sx={{
                      px: "16px",
                      py: "12px",
                      whiteSpace: "nowrap",
                      fontSize: "0.75rem",
                      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                      color: tab.active
                        ? "hsl(145, 63%, 42%)"
                        : "hsla(0, 0%, 98%, 0.4)",
                      borderBottom: tab.active
                        ? "2px solid hsl(145, 63%, 42%)"
                        : "2px solid transparent",
                    }}
                  >
                    {tab.label}
                  </Box>
                ))}
              </Box>

              {/* Search bar area */}
              <Box sx={{ px: "16px", pt: "12px", pb: "8px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <Box sx={{ display: "flex", gap: "8px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        gap: "8px",
                        px: "12px",
                        py: "6px",
                        borderRadius: "12px",
                        bgcolor: "hsla(0, 0%, 98%, 0.05)",
                        border: "1px solid hsla(0, 0%, 98%, 0.08)",
                      }}
                    >
                      <Search size={11} color="hsla(0, 0%, 98%, 0.3)" />
                      <Box
                        component="span"
                        sx={{
                          fontSize: "9px",
                          color: "hsla(0, 0%, 98%, 0.3)",
                          fontFamily:
                            "Segoe UI Regular, system-ui, sans-serif",
                        }}
                      >
                        {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.search_placeholder" })}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        px: "12px",
                        py: "6px",
                        borderRadius: "12px",
                        bgcolor: "hsla(0, 0%, 98%, 0.05)",
                        border: "1px solid hsla(0, 0%, 98%, 0.08)",
                      }}
                    >
                      <MapPin size={10} color="hsla(0, 0%, 98%, 0.3)" />
                      <Box
                        component="span"
                        sx={{
                          fontSize: "9px",
                          color: "hsla(0, 0%, 98%, 0.3)",
                          fontFamily:
                            "Segoe UI Regular, system-ui, sans-serif",
                        }}
                      >
                        {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.location_placeholder" })}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Box
                      component="span"
                      sx={{
                        px: "16px",
                        py: "4px",
                        borderRadius: "9999px",
                        bgcolor: "hsl(145, 63%, 42%)",
                        color: "hsl(0, 0%, 98%)",
                        fontSize: "9px",
                        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                      }}
                    >
                      {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.advanced_filters" })}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "10px",
                        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                        color: "hsla(0, 0%, 98%, 0.7)",
                      }}
                    >
                      {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.candidates_label" })}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: "10px",
                        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                        color: "hsl(145, 63%, 42%)",
                        bgcolor: "hsla(145, 63%, 42%, 0.15)",
                        px: "8px",
                        py: "2px",
                        borderRadius: "9999px",
                      }}
                    >
                      {formatNumber(candidatesCount)}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Main content: split list + detail */}
              <Box
                sx={{
                  display: "flex",
                  borderTop: "1px solid hsla(0, 0%, 98%, 0.08)",
                  height: "320px",
                }}
              >
                {/* Left – candidate list */}
                <Box
                  sx={{
                    width: "45%",
                    borderRight: "1px solid hsla(0, 0%, 98%, 0.08)",
                    overflow: "hidden",
                  }}
                >
                  {candidates.map((c, i) => (
                    <Box
                      component={motion.div}
                      key={i}
                      animate={{
                        backgroundColor:
                          i === activeCandidate
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(255,255,255,0)",
                      }}
                      transition={{ duration: 0.4 }}
                      onClick={() => setActiveCandidate(i)}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                        px: "12px",
                        py: "8px",
                        borderBottom: "1px solid hsla(0, 0%, 98%, 0.06)",
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        component="img"
                        src={c.avatar}
                        alt={c.name}
                        sx={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "9999px",
                          objectFit: "cover",
                          flexShrink: 0,
                          mt: "2px",
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            component="p"
                            sx={{
                              fontSize: "10px",
                              fontFamily:
                                "Segoe UI Bold, system-ui, sans-serif",
                              color: "hsl(0, 0%, 98%)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              m: 0,
                            }}
                          >
                            {c.name}
                          </Box>
                        </Box>
                        <Box
                          component="p"
                          sx={{
                            fontSize: "8px",
                            color: "hsla(0, 0%, 98%, 0.4)",
                            m: 0,
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {c.role}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            mt: "2px",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              fontSize: "7px",
                              color: "hsla(0, 0%, 98%, 0.3)",
                              bgcolor: "hsla(0, 0%, 98%, 0.06)",
                              px: "6px",
                              py: "2px",
                              borderRadius: "4px",
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {c.type}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontSize: "7px",
                              color: "hsla(0, 0%, 98%, 0.3)",
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {c.exp}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontSize: "7px",
                              color: "hsla(0, 0%, 98%, 0.3)",
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {c.location}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Right – candidate detail */}
                <Box sx={{ width: "55%", overflow: "hidden", p: "12px", position: "relative" }}>
                  <AnimatePresence mode="wait">
                    <Box
                      component={motion.div}
                      key={activeCandidate}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        position: "absolute",
                        top: "12px",
                        left: "12px",
                        right: "12px",
                      }}
                    >
                      {/* Profile header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Box
                          component="img"
                          src={candidates[activeCandidate].avatar}
                          alt={candidates[activeCandidate].name}
                          sx={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "9999px",
                            objectFit: "cover",
                          }}
                        />
                        <Box>
                          <Box
                            component="p"
                            sx={{
                              fontSize: "11px",
                              fontFamily:
                                "Segoe UI Bold, system-ui, sans-serif",
                              color: "hsl(0, 0%, 98%)",
                              m: 0,
                            }}
                          >
                            {candidates[activeCandidate].name}
                          </Box>
                          <Box
                            component="p"
                            sx={{
                              fontSize: "9px",
                              color: "hsla(0, 0%, 98%, 0.4)",
                              m: 0,
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {candidates[activeCandidate].role}
                          </Box>
                        </Box>
                      </Box>

                      {/* Tags */}
                      <Box sx={{ display: "flex", gap: "6px" }}>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "8px",
                            fontFamily:
                              "Segoe UI Bold, system-ui, sans-serif",
                            px: "8px",
                            py: "2px",
                            borderRadius: "9999px",
                            border:
                              "1px solid hsla(145, 63%, 42%, 0.3)",
                            color: "hsl(145, 63%, 42%)",
                          }}
                        >
                          {candidates[activeCandidate].location}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "8px",
                            fontFamily:
                              "Segoe UI Bold, system-ui, sans-serif",
                            px: "8px",
                            py: "2px",
                            borderRadius: "9999px",
                            border:
                              "1px solid hsla(145, 63%, 42%, 0.3)",
                            color: "hsl(145, 63%, 42%)",
                          }}
                        >
                          {candidates[activeCandidate].exp}
                        </Box>
                      </Box>

                      {/* Coordonnées */}
                      <Box
                        sx={{
                          borderRadius: "8px",
                          bgcolor: "hsla(0, 0%, 98%, 0.04)",
                          border: "1px solid hsla(0, 0%, 98%, 0.08)",
                          p: "8px",
                        }}
                      >
                        <Box
                          component="p"
                          sx={{
                            fontSize: "9px",
                            fontFamily:
                              "Segoe UI Bold, system-ui, sans-serif",
                            color: "hsl(0, 0%, 98%)",
                            mb: "4px",
                            mt: 0,
                          }}
                        >
                          {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.contact_details" })}
                        </Box>
                        <Box
                          component="p"
                          sx={{
                            fontSize: "8px",
                            color: "hsla(0, 0%, 98%, 0.5)",
                            m: 0,
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          {candidates[activeCandidate].name
                            .toLowerCase()
                            .replace(" ", ".")}
                          @example.com
                        </Box>
                        <Box
                          component="p"
                          sx={{
                            fontSize: "8px",
                            color: "hsla(0, 0%, 98%, 0.5)",
                            m: 0,
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                          }}
                        >
                          +32 321{" "}
                          {String(12 + activeCandidate * 11).padStart(
                            2,
                            "0"
                          )}{" "}
                          {String(34 + activeCandidate * 7).padStart(
                            2,
                            "0"
                          )}{" "}
                          {String(56 + activeCandidate * 3).padStart(
                            2,
                            "0"
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "12px",
                            mt: "4px",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              fontSize: "7px",
                              color: "hsla(0, 0%, 98%, 0.4)",
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.region_label" })}{" "}
                            {candidates[activeCandidate].location}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontSize: "7px",
                              color: "hsla(0, 0%, 98%, 0.4)",
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.experience_label" })}{" "}
                            {candidates[activeCandidate].exp}
                          </Box>
                        </Box>
                      </Box>

                      {/* Préférences */}
                      <Box
                        sx={{
                          borderRadius: "8px",
                          bgcolor: "hsla(0, 0%, 98%, 0.04)",
                          border: "1px solid hsla(0, 0%, 98%, 0.08)",
                          p: "8px",
                        }}
                      >
                        <Box
                          component="p"
                          sx={{
                            fontSize: "9px",
                            fontFamily:
                              "Segoe UI Bold, system-ui, sans-serif",
                            color: "hsl(0, 0%, 98%)",
                            mb: "4px",
                            mt: 0,
                          }}
                        >
                          {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.preferences_label" })}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                          }}
                        >
                          <Box
                            component="p"
                            sx={{
                              fontSize: "8px",
                              color: "hsla(0, 0%, 98%, 0.5)",
                              m: 0,
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.job_label" })} {candidates[activeCandidate].role}
                          </Box>
                          <Box
                            component="p"
                            sx={{
                              fontSize: "8px",
                              color: "hsla(0, 0%, 98%, 0.5)",
                              m: 0,
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.region_label" })}{" "}
                            {candidates[activeCandidate].location}
                          </Box>
                          <Box
                            component="p"
                            sx={{
                              fontSize: "8px",
                              color: "hsla(0, 0%, 98%, 0.5)",
                              m: 0,
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.type_label" })} {candidates[activeCandidate].type}
                          </Box>
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            mt: "6px",
                            fontSize: "8px",
                            fontFamily:
                              "Segoe UI Bold, system-ui, sans-serif",
                            color: "hsl(145, 63%, 42%)",
                            bgcolor: "hsla(145, 63%, 42%, 0.1)",
                            px: "8px",
                            py: "2px",
                            borderRadius: "4px",
                          }}
                        >
                          <Download size={8} /> {intl.formatMessage({ id: "landing_recruiter.recruiter_hero.download_cv" })}
                        </Box>
                      </Box>
                    </Box>
                  </AnimatePresence>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RecruiterHero;
